<template>
    <Popover v-model:open="isOpen" :modal="popoverModal">
        <PopoverAnchor>
            <PopoverTrigger as-child>
                <Button
                    :class="[!modelValue ? 'text-muted' : '', isOpen ? 'border-accent!' : '']"
                    class="flex h-12 w-full items-center justify-start border-2 border-[#d8dbe0] bg-white! px-2 py-3 text-left font-normal !text-[#525252] focus:!bg-white"
                    variant="outline"
                >
                    <CalendarIcon class="mr-2 h-4 w-4 text-[#8490a1]" />
                    <span class="text-[#8490a1]">{{ selectedDateRange }}</span>
                </Button>
            </PopoverTrigger>
        </PopoverAnchor>
        <PopoverContent :disable-popover-portal="!teleportPopover" :side-offset="4" align="start" class="w-auto border-2 border-accent p-0">
            <RangeCalendar
                v-model="rangeDV"
                :is-date-disabled="isDateDisabled"
                :locale="currentLocale"
                :max-value="maxValue"
                :min-value="minValue"
                :number-of-months="1"
                :week-starts-on="1"
                class="w-100"
                initial-focus
            />
        </PopoverContent>
    </Popover>
</template>
<script setup>
import { computed, getCurrentInstance, ref, watch } from 'vue';
import { RangeCalendar } from '@/Components/ui/range-calendar';
import { DateTime } from 'luxon';
import fullDate from '@/filters/fullDate.js';
import { getLocalTimeZone, parseDate } from '@internationalized/date';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/Components/ui/popover';
import { Calendar as CalendarIcon } from '@lucide/vue';
import { Button } from '@/Components/ui/button';

defineOptions({ name: 'date-filter' });

const props = defineProps({
    modelValue: { type: Object },
    attributes: { type: Array },
    disabledDates: { type: Array, default: () => [] },
    selectionMode: { type: String, default: 'daily' },
    label: { type: String },
    maxDate: { type: String },
    minDate: { type: String },
    popoverModal: { type: Boolean, default: false },
    // Render the calendar popover in a body-level portal instead of inline —
    // see MultiSelectDropdown.teleportPopover (container-type ancestors break
    // inline anchored positioning).
    teleportPopover: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const isOpen = ref(false);
const dateRange = ref({ start: null, end: null });
const rangeDV = ref({ start: undefined, end: undefined });

const instance = getCurrentInstance();
const currentLocale = computed(() => instance?.proxy?.currentLocale || 'en');

const appliedAttributes = computed(() => {
    return props.attributes && props.attributes.length
        ? props.attributes
        : [
              {
                  key: 'selected',
                  highlight: true,
                  dates: props.modelValue,
              },
          ];
});

const maxValue = computed(() => (props.maxDate ? parseDate(props.maxDate) : undefined));
const minValue = computed(() => (props.minDate ? parseDate(props.minDate) : undefined));

const selectedDateRange = computed(() => {
    const formattedStartDate = fullDate(props.modelValue?.start, currentLocale.value);
    const formattedEndDate = fullDate(props.modelValue?.end, currentLocale.value);

    if (!formattedStartDate && !formattedEndDate) {
        return props.label;
    }
    if (!formattedEndDate) {
        return '>= ' + formattedStartDate;
    }
    if (!formattedStartDate) {
        return '<= ' + formattedEndDate;
    }
    return `${formattedStartDate} - ${formattedEndDate}`;
});

watch(
    () => props.modelValue,
    (value) => {
        dateRange.value = {
            start: !!value?.start ? new Date(value.start) : null,
            end: !!value?.end ? new Date(value.end) : null,
        };

        const nextStartIso = typeof value?.start === 'string' ? value.start : null;
        const nextEndIso = typeof value?.end === 'string' ? value.end : null;

        const curStartIso = dvToISO(rangeDV.value.start);
        const curEndIso = dvToISO(rangeDV.value.end);

        if (nextStartIso !== curStartIso) {
            rangeDV.value.start = nextStartIso ? parseDate(nextStartIso) : undefined;
        }
        if (nextEndIso !== curEndIso) {
            rangeDV.value.end = nextEndIso ? parseDate(nextEndIso) : undefined;
        }
    },
    { immediate: true },
);

watch(
    () => rangeDV.value,
    (range) => {
        const startIso = dvToISO(range?.start);
        const endIso = dvToISO(range?.end);

        if (!startIso || !endIso) return;
        if (startIso === props.modelValue?.start && endIso === props.modelValue?.end) return;

        emit('update:modelValue', { start: startIso, end: endIso });
    },
    { deep: true },
);

function dvToISO(dv) {
    if (!dv) return null;
    const js = typeof dv.toDate === 'function' ? dv.toDate(getLocalTimeZone()) : null;
    if (!js) return null;
    return DateTime.fromJSDate(js).toFormat('yyyy-MM-dd');
}

function isDateDisabled(dv) {
    if (!Array.isArray(props.disabledDates) || props.disabledDates.length === 0) return false;
    const js = typeof dv?.toDate === 'function' ? dv.toDate(getLocalTimeZone()) : null;
    if (!js) return false;
    js.setHours(0, 0, 0, 0);

    return props.disabledDates.some((rule) => {
        if (!rule) return false;
        // Exact date instance disables that day
        if (rule instanceof Date) {
            const d = new Date(rule);
            d.setHours(0, 0, 0, 0);
            return js.getTime() === d.getTime();
        }
        // Range object: { start?: Date, end?: Date }
        const start = rule.start instanceof Date ? new Date(rule.start) : null;
        const end = rule.end instanceof Date ? new Date(rule.end) : null;
        if (start) start.setHours(0, 0, 0, 0);
        if (end) end.setHours(0, 0, 0, 0);

        if (start && end) {
            return js >= start && js <= end;
        }
        if (start && !end) {
            // Disable from start forward (e.g., future dates)
            return js >= start;
        }
        if (!start && end) {
            // Disable up to end (e.g., past dates)
            return js <= end;
        }
        return false;
    });
}
</script>
